import {Button} from 'react-bootstrap';
import React, {useEffect, useState} from 'react';
import LargeCard from '../main/large-card';
import ValidatorFormGroup from '../main/validator-form-group';
import {TECHNICIAN_BOARD_LINK} from '../../constants';
import {LocationDto} from '../../store/locationsSlice';
import useTranslate from '../useTranslate';
import Modal from '../util/modal';
import {usePbDispatch, usePbSelector} from '../../store/pbStoreHooks';
import {
    CreateInstallationLink,
    ExtendInstallationLink,
    GetInstallationLink,
    RevokeInstallationLink,
    SendInstallationLink,
} from '../../store/installationLinkSlice';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faCopy} from "@fortawesome/pro-light-svg-icons";
import {DateTime} from "luxon";
import OutlinedButton from "../util/outlined-button";
import {PbRequest} from "../../store/pbRequest";
import ConfirmButton from "../util/confirm-button";
import {pbRequestReset} from "../../store/pbRequestSlice";
import {Link} from "react-router";

interface Props {
    location: LocationDto;
}

const initialValidityDays = 60;
const extendValidityDays = 30;

function calculateExpireTimestamp(days: number) {
    return DateTime.local().plus({days}).toMillis();
}

export default function InstallationLinkCard(props: Readonly<Props>) {
    const strings = useTranslate('InstallationLinkCard');
    const dispatch = usePbDispatch();
    const { installationLink } = usePbSelector(
        (state) => state.installationLink
    );
    const requestSend = usePbSelector(state => state.pbRequest[PbRequest.SEND_INSTALLATION_LINK]);
    const isAdmin = usePbSelector(state => state.partnerManager.isAdminAccountManager)

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipient, setEmailRecipient] = useState('');
    const [lastEmailRecipient, setLastEmailRecipient] = useState('');

    useEffect(() => {
        dispatch(GetInstallationLink(props.location.id));
        return () => {
            dispatch(pbRequestReset(PbRequest.SEND_INSTALLATION_LINK));
        }
    }, [props.location.id]);

    const technicianBoardLaundryRoomLink = props.location.external_id
        ? `${TECHNICIAN_BOARD_LINK}/laundry-room/${props.location.external_id}`
        : TECHNICIAN_BOARD_LINK;

    return (
        <>
            <LargeCard title={strings.title}>
                <p>{strings.description}</p>
                {installationLink && (
                    <>
                        <div className="mt-15">
                            <Link className="icon font-md" to={buildInstallationLinkUrl()} target="_blank">
                                {strings.installationLink}
                            </Link>
                            <Button className="btn btn-link btn-action ml-10"
                                    onClick={handleCopy}>
                                <FontAwesomeIcon icon={faCopy} className="icon-for-button"/>
                            </Button>
                        </div>
                        <div style={{marginTop: '-16px'}}>{buildExpirationText()}</div>
                        <div className="mt-20" style={{display: 'flex', gap: '10px'}}>
                            <OutlinedButton onClick={() => setShowEmailModal(true)}>
                                {strings.emailAction}
                            </OutlinedButton>
                            {isExtensionAdmissible() && (
                                <OutlinedButton onClick={handleExtend}>
                                    {strings.extendAction}
                                </OutlinedButton>
                            )}
                            <ConfirmButton onConfirmedAction={handleRevoke} confirmTitle={strings.revokeConfirmTitle}
                                           confirmMessage={strings.revokeConfirmMessage}
                                           confirmAction={strings.revokeAction}
                                           rejectAction={strings.modalClose}>
                                <OutlinedButton danger>
                                    {strings.revokeAction}
                                </OutlinedButton>
                            </ConfirmButton>
                        </div>
                        {requestSend.status === 'SUCCESS' && lastEmailRecipient && (
                            <div className="mt-10 mb-0">
                                <FontAwesomeIcon icon={faCheck}/> {strings.emailSuccess} {lastEmailRecipient}
                            </div>
                        )}
                    </>
                )}
                {!installationLink && (
                    <Button
                        onClick={handleCreateLink}
                        className="btn btn-primary mt-10 mb-20"
                    >
                        {strings.createAction}
                    </Button>
                )}
                {isAdmin && (
                    <p className="mt-10">
                        {strings.technicianBoardLinkPre}{' '}
                        <a
                            className="icon icon-sm"
                            href={technicianBoardLaundryRoomLink}
                            target="_blank"
                        >
                            {strings.technicianBoardLinkText}
                        </a>
                    </p>)}
            </LargeCard>
            <Modal show={showEmailModal} onClose={handleModalClose}>
                <form onSubmit={handleSend}>
                    <h2>{strings.emailTitle}</h2>
                    <p className="mt-2 mb-lg-4">{strings.emailMessage}</p>
                    <ValidatorFormGroup
                        label={strings.recipientLabel}
                        errorLabel={strings.recipientInvalid}
                    >
                        <input
                            type="email"
                            className={'form-control'}
                            onChange={(e) => setEmailRecipient(e.target.value)}
                            value={emailRecipient}
                        />
                    </ValidatorFormGroup>
                    <Button type="submit" className="btn btn-primary" disabled={!emailRecipient}>
                        {strings.emailAction}
                    </Button>{' '}
                    <Button className={'btn'} onClick={handleModalClose}>
                        {strings.modalClose}
                    </Button>
                </form>
            </Modal>
        </>
    );

    function handleCopy() {
        navigator.clipboard.writeText(buildInstallationLinkUrl());
    }

    function buildInstallationLinkUrl() {
        return `${TECHNICIAN_BOARD_LINK}/at/${installationLink.token}`;
    }

    function calculateRemainingDays() {
        const rounded = Math.round(DateTime.fromMillis(installationLink.expire_timestamp).diff(
            DateTime.local(),
            "days"
        ).days);
        return Math.max(rounded, 0);
    }
    function buildExpirationText() {
        if (installationLink.status === 'INVALID') {
            return `(${strings.invalid})`;
        }
        const remainingDays = calculateRemainingDays();
        return `(${strings.expires} ${remainingDays} ${remainingDays === 1 ? strings.day : strings.days})`;
    }

    function isExtensionAdmissible() {
        return calculateRemainingDays() < extendValidityDays;
    }

    function handleExtend() {
        const newExpireTimestamp = calculateExpireTimestamp(extendValidityDays);
        dispatch(ExtendInstallationLink(props.location.id, {expire_timestamp: newExpireTimestamp}));
    }

    function handleRevoke() {
        dispatch(RevokeInstallationLink(props.location.id));
    }

    function handleSend() {
        dispatch(SendInstallationLink(props.location.id, {email: emailRecipient}));
        setLastEmailRecipient(emailRecipient);
        handleModalClose();
    }

    async function handleCreateLink() {
        const expireTimestamp = calculateExpireTimestamp(initialValidityDays);
        await dispatch(CreateInstallationLink(props.location.id, {expire_timestamp: expireTimestamp}));
        handleModalClose();
    }

    function handleModalClose() {
        setShowEmailModal(false);
    }
}
